import React from 'react';
import moment from 'moment';
import { Event } from '@mui/icons-material';
import { blue, grey } from '@mui/material/colors';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { DateFormats, NO_DATA } from 'constants/common';
import { DealActivityParticipationType } from '@trnsact/trnsact-shared-types';

import { ActivityCardProps } from '../../types';
import { CardContainer } from './common/CardContainer';
import { DealEventActivity } from '../../api/types';
import { useEventIdParam } from '../../calendar/hooks';
import { StatusCell } from '../../calendar/ui/table/cells/StatusCell';

interface Props extends Pick<ActivityCardProps<DealEventActivity>, 'entity'> {
  opportunityLabel?: string;
}

interface InfoProps {
  label: string;
  value: string;
}

const Info = ({ label, value }: InfoProps) => (
  <Stack>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

export const EventActivityCard = ({ entity, opportunityLabel }: Props) => {
  const { openEvent } = useEventIdParam();

  const { id, subject, allDay, startAt, startDate, participants, notes, description, activityStatus, opportunityId } =
    entity;

  const start = allDay ? startDate : startAt;
  const dateText = start
    ? moment(start).format(allDay ? DateFormats.Medium : DateFormats.DateTimeMedium12Hour)
    : NO_DATA;

  const owner = participants.find(p => p.participationType === DealActivityParticipationType.Internal)?.fullName;
  const contact = participants.find(p => p.participationType === DealActivityParticipationType.External)?.fullName;

  // Notes come newest first
  const notePreview = notes[0]?.content || description;

  return (
    <Box sx={{ cursor: 'pointer' }} onClick={() => openEvent(id)}>
      <CardContainer>
        <Stack direction="row" gap="1rem" alignItems="flex-start">
          <Box sx={{ display: 'flex', padding: '0.6rem', borderRadius: '100%', backgroundColor: blue[500] }}>
            <Event sx={{ color: 'white' }} />
          </Box>

          <Stack gap="0.25rem" flex={1}>
            <Typography variant="h6" component="span">
              {subject}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {dateText}
            </Typography>
          </Stack>

          <StatusCell status={activityStatus} />
        </Stack>

        <Stack direction="row" gap="2rem" flexWrap="wrap">
          <Info label="Owner" value={owner || NO_DATA} />
          <Info label="Contact" value={contact || NO_DATA} />
          {opportunityId && <Info label="Opportunity" value={opportunityLabel || opportunityId} />}
        </Stack>

        {notePreview && (
          <>
            <Divider />

            <Stack gap="0.3rem" sx={{ border: `1px solid ${grey[300]}`, borderRadius: '0.3rem', padding: '0.5rem' }}>
              <Typography variant="subtitle2" component="span">
                Notes
              </Typography>
              <Typography
                component="span"
                sx={{
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {notePreview}
              </Typography>
            </Stack>
          </>
        )}
      </CardContainer>
    </Box>
  );
};
